import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import ChemSearchModal from 'chemistry/ChemSearchModal';
import {Filter, SearchParams} from 'search/SearchConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import runModal from 'ui-library/runModal';

import {
  extractMolQuery,
  isCompleteChemSearchParams,
  ToggleHighlightParams,
  updateChemSearchParams,
} from './ChemSearchUtils';

export interface ChemSearchButtonProps {
  className?: string;
  onUpdateFilters: (filters: Array<Filter>) => void;
  searchParams: SearchParams;
  toggleHighlightParams: ToggleHighlightParams;
}

export default function ChemSearchButton({
  className,
  onUpdateFilters,
  searchParams,
  toggleHighlightParams,
}: ChemSearchButtonProps): JSX.Element | null {
  const mol = extractMolQuery(searchParams);
  const [hover, setHover] = useState(false);
  return (
    <Button
      aria-label="Chemical structure search"
      buttonStyle={ButtonStyle.TERTIARY_WHITE}
      className={classNames(css(styles.button), className, {
        [css(styles.buttonHover)]: Boolean(hover),
      })}
      size={ButtonSize.SMALL}
      icon={FolderItemIcon.SmallMolecule}
      onClick={async () => {
        const chemSearchParams = await runModal(ChemSearchModal, {
          initialMol: mol,
          toggleHighlightParams,
          searchParams,
        });
        if (chemSearchParams && isCompleteChemSearchParams(chemSearchParams)) {
          onUpdateFilters(updateChemSearchParams(searchParams, chemSearchParams).filters);
        }
      }}
      onMouseOver={() => {
        setHover(true);
      }}
      onMouseOut={() => {
        setHover(false);
      }}
      dataTestAttrs={{component: 'ChemSearchButton', element: 'root'}}
    />
  );
}

// The SMALL button is slightly too big to fit inside the search box, but the MINI button makes it impossible
// to tell what icon the molecule is. So we squish the padding slightly.
const styles = StyleSheet.create({
  button: {
    border: 'none',
    borderRadius: '2px',
    margin: '2px',
    height: '24px',
    padding: '2px 6px',
  },
  buttonHover: {
    backgroundColor: GRAY_LIGHT,
  },
});
